import React, {ReactNode, useEffect, useState} from "react";

import cx from "classnames";

import NomiconIcon from "@components/NomiconIcon";
import TransparentButton from "@components/TransparentButton";

import styles from "./CollapsibleSection.scss";

function CollapsibleTitleWithBorder({
  isCollapsed,
  toggle,
  title,
  titleClassName,
}: {
  isCollapsed: boolean;
  toggle: () => void;
  title: string | ReactNode;
  titleClassName?: string;
}): JSX.Element {
  return (
    <TransparentButton
      className={cx(styles.headingWithBorder, titleClassName)}
      onClick={toggle}
      aria-expanded={!isCollapsed}
    >
      {title}
      <NomiconIcon
        icon="nomicon-caret-up"
        className={cx(styles.icon, isCollapsed && styles.iconCollapsed)}
      />
    </TransparentButton>
  );
}

type CollapsibleSectionProps = {
  title: string | ReactNode;
  children: ReactNode;
  sectionClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
  onToggle?: (isCollapsed: boolean) => void;
  withBorder?: boolean;
  isCollapsed?: boolean;
};

export default function CollapsibleSection({
  title,
  children,
  sectionClassName,
  titleClassName,
  contentClassName,
  onToggle,
  withBorder,
  isCollapsed: isCollapsedProp = true,
}: CollapsibleSectionProps): JSX.Element {
  const [isCollapsed, setIsCollapsed] = useState(isCollapsedProp);

  const toggle = () => {
    if (onToggle) {
      onToggle(!isCollapsed);
    }
    setIsCollapsed(!isCollapsed);
  };

  // used to trigger toggle() from outside the component;
  useEffect(() => {
    if (isCollapsed !== isCollapsedProp) {
      toggle();
    }
  }, [isCollapsedProp]);

  return (
    <div
      className={cx(
        styles.container,
        sectionClassName,
        withBorder && styles.containerWithBorder
      )}
    >
      {withBorder ? (
        <CollapsibleTitleWithBorder
          titleClassName={titleClassName}
          isCollapsed={isCollapsed}
          toggle={toggle}
          title={title}
        />
      ) : (
        <button
          type="button"
          aria-expanded={!isCollapsed}
          className={cx(styles.heading, titleClassName)}
          onClick={toggle}
        >
          <NomiconIcon
            icon="nomicon-caret-down"
            className={cx(styles.icon, isCollapsed && styles.iconCollapsed)}
          />
          {title}
        </button>
      )}
      {isCollapsed || (
        <div className={cx(styles.content, contentClassName)}>{children}</div>
      )}
    </div>
  );
}
